import type { ReactNode } from 'react'
import { Save, Send, Link2, Presentation } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import type { ComboVariation, PropostaItem } from '@/types'
import type { ComboData, ComboValue } from './types'
import { ItemTable } from './ItemTable'
import { ComboSection } from './ComboSection'
import { ProposalTotalsSection } from './ProposalTotalsSection'
import { maxValidadeDateInput, toDateInputValue } from './proposalFormState'
import { PROPOSAL_FORM_ACTIONS_ENABLED } from './proposalFeatureFlags'

interface ProposalFormProps {
  editingId: string | null
  itens: PropostaItem[]
  combos: ComboData[]
  comboValues: ComboValue[]
  comboVariations: ComboVariation[]
  validadeAte: string
  observacoes: string
  desconto: number
  saving: boolean
  generatingLink: boolean
  canEdit: boolean
  onValidadeChange: (v: string) => void
  onDescontoChange: (v: number) => void
  onObservacoesChange: (v: string) => void
  onRemoveItem: (id: string) => void
  onUpdateQty: (id: string, qty: number) => void
  onAddCombo: () => void
  onToggleComboItem: (comboId: string, itemId: string) => void
  onComboDesconto: (comboId: string, val: number) => void
  onRemoveCombo: (comboId: string) => void
  onSetComboFinalPrice: (comboGroupId: string, price: number) => void
  onCancelEdit: () => void
  onSaveRascunho: () => void
  onEnviarProposta: () => void
  onGerarLink: () => void
  onEditItem?: (item: PropostaItem, index: number) => void
  onOpenStudio?: (item: PropostaItem, index: number) => void
  onCloseEditor?: () => void
  comboVariationsSlot?: ReactNode
  footerExtra?: ReactNode
}

export function ProposalForm({
  editingId,
  itens,
  combos,
  comboValues,
  comboVariations,
  validadeAte,
  observacoes,
  desconto,
  saving,
  generatingLink,
  canEdit,
  onValidadeChange,
  onDescontoChange,
  onObservacoesChange,
  onRemoveItem,
  onUpdateQty,
  onAddCombo,
  onToggleComboItem,
  onComboDesconto,
  onRemoveCombo,
  onSetComboFinalPrice,
  onCancelEdit,
  onSaveRascunho,
  onEnviarProposta,
  onGerarLink,
  onEditItem,
  onOpenStudio,
  onCloseEditor,
  comboVariationsSlot,
  footerExtra,
}: ProposalFormProps) {
  const today = toDateInputValue(new Date())
  const minValidade = validadeAte && validadeAte < today ? validadeAte : today
  const maxValidade = maxValidadeDateInput()

  return (
    <div id="proposal-form-editor" className="glass-card p-4 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-xs font-bold text-accent uppercase tracking-wider">
            {editingId ? 'Editar proposta' : 'Nova proposta'}
          </h3>
          <p className="text-[10px] text-text-muted mt-0.5">
            Adicione planos, salve rascunho ou envie. Combos e opções são opcionais.
          </p>
        </div>
        {editingId ? (
          <button onClick={onCancelEdit} className="text-xs text-text-muted hover:text-text-primary transition-colors shrink-0">
            Cancelar edição
          </button>
        ) : onCloseEditor ? (
          <button onClick={onCloseEditor} className="text-xs text-text-muted hover:text-text-primary transition-colors shrink-0">
            Fechar
          </button>
        ) : null}
      </div>

      <div className="space-y-3">
        <h4 className="text-[11px] font-semibold text-text-label uppercase tracking-wider">Itens da Proposta</h4>
        <ItemTable
          itens={itens}
          combos={combos}
          canEdit={canEdit}
          onRemoveItem={onRemoveItem}
          onUpdateQty={onUpdateQty}
          onEditItem={canEdit ? onEditItem : undefined}
          onOpenStudio={onOpenStudio}
        />
      </div>

      <ComboSection
        itens={itens}
        combos={combos}
        comboValues={comboValues}
        canEdit={canEdit}
        onAddCombo={onAddCombo}
        onToggleComboItem={onToggleComboItem}
        onComboDesconto={onComboDesconto}
        onRemoveCombo={onRemoveCombo}
        onSetComboFinalPrice={onSetComboFinalPrice}
      />

      {comboVariationsSlot}

      {/* Configuração */}
      <div className="space-y-3 pt-2 border-t border-white/[0.06]">
        <h4 className="text-[11px] font-semibold text-text-label uppercase tracking-wider">Configuração</h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] text-text-label uppercase tracking-wider font-medium">Validade até</label>
            <input
              type="date"
              min={minValidade}
              max={maxValidade}
              value={validadeAte}
              onChange={(e) => {
                const next = e.target.value
                if (!next) return
                onValidadeChange(next)
              }}
              className="w-full bg-white/[0.04] border border-white/[0.06] text-text-primary text-xs rounded-md px-3 py-2 outline-none focus:border-accent"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] text-text-label uppercase tracking-wider font-medium">Desconto (%)</label>
            <input
              type="number" min={0} max={100} step={0.5} value={desconto}
              onChange={(e) => onDescontoChange(Math.min(100, Math.max(0, Number(e.target.value))))}
              className="w-full bg-white/[0.04] border border-white/[0.06] text-text-primary text-xs rounded-md px-3 py-2 outline-none focus:border-accent"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] text-text-label uppercase tracking-wider font-medium">Observações</label>
          <textarea
            value={observacoes} onChange={(e) => onObservacoesChange(e.target.value)}
            placeholder="Condições especiais, termos, prazo de entrega..." rows={3}
            className="w-full bg-white/[0.04] border border-white/[0.06] text-text-primary text-xs rounded-md px-3 py-2 outline-none focus:border-accent resize-none"
          />
        </div>
      </div>

      <ProposalTotalsSection
        itens={itens}
        comboValues={comboValues}
        comboVariations={comboVariations}
        desconto={desconto}
      />

      {/* Ações — fluxo principal + ferramentas */}
      {canEdit && PROPOSAL_FORM_ACTIONS_ENABLED && (
        <div className="pt-4 border-t border-white/[0.08] space-y-3">
          <div className="flex items-baseline justify-between gap-2">
            <h4 className="text-[11px] font-semibold text-text-label uppercase tracking-wider">
              Ações
            </h4>
            <span className="text-[10px] text-text-muted tabular-nums">
              {itens.length} {itens.length === 1 ? 'item' : 'itens'}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="secondary"
              size="sm"
              icon={<Save size={13} />}
              loading={saving}
              disabled={itens.length === 0}
              onClick={onSaveRascunho}
              className="w-full justify-center"
            >
              Salvar rascunho
            </Button>
            <Button
              variant="primary"
              size="sm"
              icon={<Send size={13} />}
              loading={saving}
              disabled={itens.length === 0}
              onClick={onEnviarProposta}
              className="w-full justify-center"
            >
              Enviar proposta
            </Button>
          </div>

          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5 space-y-2">
            <p className="text-[10px] font-medium text-text-muted uppercase tracking-wider px-0.5">
              Ferramentas
            </p>
            <div
              className={
                onOpenStudio ? 'grid grid-cols-2 gap-2' : 'grid grid-cols-1 gap-2'
              }
            >
              <Button
                variant="ghost"
                size="sm"
                icon={<Link2 size={13} />}
                loading={generatingLink}
                disabled={itens.length === 0}
                onClick={onGerarLink}
                className="w-full justify-center border border-white/[0.06] bg-white/[0.03] hover:bg-white/[0.06]"
              >
                Gerar link
              </Button>
              {onOpenStudio && (
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<Presentation size={13} />}
                  disabled={itens.length === 0}
                  onClick={() => onOpenStudio(itens[0] || ({ id: 'mock' } as PropostaItem), 0)}
                  className="w-full justify-center border border-orange-500/25 bg-orange-500/[0.06] text-orange-400 hover:border-orange-400/40 hover:bg-orange-500/10 hover:text-orange-300"
                >
                  Apresentação
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {PROPOSAL_FORM_ACTIONS_ENABLED && footerExtra}
    </div>
  )
}
